"use client";

// EXPERIMENTAL game-like battle scene for the live view (opt-in via ?scene=1).
// Instead of data panels, it recreates the in-game combat layout: a top HUD
// bar, the player as a token on the left and the enemies as tokens on the
// right, each with HP / block / powers, the enemies showing their intent, and
// the hand along the bottom. Combat only for now; other screens fall through to
// the normal layout. All driven by the same presence fields the panels use.
import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useT } from "@/lib/i18n";
import { imageUrl } from "@/lib/image-url";
import { RichDescriptionSimple } from "@/app/components/RichDescription";
import LiveMap from "../LiveMap";
import { CardPill, PotionPill, RelicPill, cleanId, displayName } from "../../runs/[hash]/RunPills";
import { CharacterIcon, EnemyCircle, LiveCardImg, characterName, enemyName, intentTitle, monsterName, namedOr, parseDeckId, powerName, screenLabel, withOrdinalKeys, type EncounterMap, type Enemy, type EnemyIntent, type LiveCatalogs, type LiveOrb, type LivePet, type LivePlayer, type LivePower, type LiveSeat, type MonsterMap, type NamedMap } from "../live-shared";
import { LiveEventPanel, LiveLootPanel, LiveShopPanel } from "../LiveEventShop";

// Map an intent category to its icon file (a few names differ from the type).
const INTENT_FILE: Record<string, string> = {
  attack: "attack",
  deathblow: "death_blow",
  defend: "defend",
  buff: "buff",
  heal: "heal",
  debuff: "debuff",
  carddebuff: "card_debuff",
  escape: "escape",
  sleep: "sleep",
  status: "status",
  hidden: "hidden",
  unknown: "hidden"
};
function intentSrc(type?: string): string {
  const key = (type || "unknown").toLowerCase();
  return imageUrl(`/static/images/intents/${INTENT_FILE[key] || "hidden"}.png`);
}

/** The enemy's next move: the intent icon with the damage (and hit count) on it. */
function IntentBadge(t0) {
  const $ = _c(19);
  const {
    intent
  } = t0;
  const t = useT();
  const dmg = intent.dmg != null ? intent.dmg : null;
  const hits = intent.hits && intent.hits > 1 ? `×${intent.hits}` : "";
  let t1;
  if ($[0] !== intent.type) {
    t1 = intentSrc(intent.type);
    $[0] = intent.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== intent.type || $[3] !== t) {
    t2 = intentTitle(intent.type, t);
    $[2] = intent.type;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== intent.type || $[6] !== t) {
    t3 = intentTitle(intent.type, t);
    $[5] = intent.type;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <img src={t1} alt={t2} title={t3} className="h-7 w-7 object-contain drop-shadow" crossOrigin="anonymous" onError={_temp} />;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== dmg || $[13] !== hits || $[14] !== intent.amount) {
    t5 = dmg != null ? <span className="text-xs font-bold tabular-nums text-danger">{dmg}{hits}</span> : intent.amount != null ? <span className="text-xs font-bold tabular-nums text-info">{intent.amount}</span> : null;
    $[12] = dmg;
    $[13] = hits;
    $[14] = intent.amount;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <span className="inline-flex flex-col items-center">{t4}{t5}</span>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

/** Buff/debuff chips as the game's power icons with their stack count. */
function _temp(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function PowerRow(t0) {
  const $ = _c(7);
  const {
    powers,
    names
  } = t0;
  if (!powers.length) {
    return null;
  }
  let t1;
  if ($[0] !== names || $[1] !== powers) {
    let t2;
    if ($[3] !== names) {
      t2 = pw => <span key={pw.id} className="relative inline-flex" title={powerName(pw.id, names)}><img src={imageUrl(`/static/images/powers/${pw.id.toLowerCase()}.png`)} alt={powerName(pw.id, names)} className="h-5 w-5 object-contain" crossOrigin="anonymous" onError={_temp2} />{pw.amount != null && pw.amount !== 0 && <span className="absolute -bottom-1 -right-1 rounded bg-scrim/70 px-0.5 text-[9px] font-bold leading-none text-on-fill tabular-nums">{pw.amount}</span>}</span>;
      $[3] = names;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = powers.map(t2);
    $[0] = names;
    $[1] = powers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-wrap justify-center gap-1">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** The player's channeled orbs: a row of orb icons (filled + empty slots), each
 * with its passive (per-turn) value. */
function _temp2(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function OrbRow(t0) {
  const $ = _c(7);
  const {
    orbs,
    slots,
    names
  } = t0;
  const t = useT();
  const total = Math.max(slots ?? 0, orbs.length);
  if (total <= 0) {
    return null;
  }
  let t1;
  if ($[0] !== total) {
    t1 = Array.from({
      length: total
    });
    $[0] = total;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== names || $[3] !== orbs || $[4] !== t || $[5] !== t1) {
    t2 = <div className="flex flex-wrap justify-center gap-1">{t1.map((_, i) => {
        const orb = orbs[i];
        return <span key={i} className="relative inline-flex" title={orb ? namedOr(orb.id, names) : t("Empty orb slot")}><img src={imageUrl(orb ? `/static/images/orbs/${cleanId(orb.id).toLowerCase()}_orb.png` : "/static/images/orbs/empty_orb.png")} alt="" className="h-7 w-7 object-contain" crossOrigin="anonymous" onError={_temp3} />{orb?.passive != null && <span className="absolute -bottom-1 -right-1 rounded bg-scrim/70 px-0.5 text-[9px] font-bold leading-none text-on-fill tabular-nums">{orb.passive}</span>}</span>;
      })}</div>;
    $[2] = names;
    $[3] = orbs;
    $[4] = t;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** HP bar + block + the optional block shield, under a token. */
function _temp3(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function Vitals(t0) {
  const $ = _c(10);
  const {
    hp,
    maxHp,
    block
  } = t0;
  const t = useT();
  const pct = hp != null && maxHp ? Math.max(0, Math.min(100, hp / maxHp * 100)) : null;
  let t1;
  if ($[0] !== hp || $[1] !== maxHp || $[2] !== pct) {
    t1 = pct != null && <div className="relative h-4 rounded bg-scrim/50 ring-1 ring-scrim/40"><div className="h-4 rounded bg-danger-fill" style={{
        width: `${pct}%`
      }} /><span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-on-fill tabular-nums drop-shadow">{hp}/{maxHp}</span></div>;
    $[0] = hp;
    $[1] = maxHp;
    $[2] = pct;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== block || $[5] !== t) {
    t2 = (block ?? 0) > 0 && <div className="mt-0.5 flex items-center justify-center gap-1 text-[11px] font-bold text-info"><img src={imageUrl("/static/images/intents/defend.png")} alt={t("block")} className="h-4 w-4 object-contain" crossOrigin="anonymous" />{block}</div>;
    $[4] = block;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="w-32 max-w-full">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/** Friendly summons beside the player token (the Necrobinder's Osty and any
 * future pet): portrait + name + HP, so a spectator can see the summon's health.
 * Dead pets drop off. `owner` co-op matching is left to the caller. */
/** A co-op teammate on the combat scene, drawn from seat vitals alone so the
 * whole party shows even when only one player runs the mod (the modded client
 * reports every seat). The beat carries no per-seat name yet, so the
 * character name stands in until the payload grows one. */
function PartyMate(t0) {
  const $ = _c(31);
  const {
    seat,
    turnSide,
    names
  } = t0;
  const t = useT();
  const down = seat.alive === false;
  const active = !down && turnSide === "player" && !seat.ended_turn;
  const t1 = `flex flex-col items-center gap-1 ${down ? "opacity-50" : ""}`;
  const t2 = `inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 bg-[var(--bg-primary)] transition ${active ? "border-[var(--accent-gold)] ring-2 ring-[var(--accent-gold)]/60" : down ? "border-danger/60 grayscale" : "border-[var(--accent-gold)]/40"}`;
  let t3;
  if ($[0] !== seat.character) {
    t3 = <CharacterIcon character={seat.character} className="h-[88%] w-[88%]" />;
    $[0] = seat.character;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== names || $[6] !== seat.character) {
    t5 = characterName(seat.character, names);
    $[5] = names;
    $[6] = seat.character;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="max-w-[7rem] truncate">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== down || $[11] !== t) {
    t7 = down && <span className="text-[10px] font-bold uppercase text-danger">{t("down")}</span>;
    $[10] = down;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== down || $[14] !== seat.ended_turn || $[15] !== t || $[16] !== turnSide) {
    t8 = !down && seat.ended_turn && turnSide === "player" && <span className="text-[10px] text-success" title={t("Turn locked in")}>✓</span>;
    $[13] = down;
    $[14] = seat.ended_turn;
    $[15] = t;
    $[16] = turnSide;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className="flex items-center gap-1 text-xs font-semibold text-[var(--text-primary)]">{t6}{t7}{t8}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== seat.block || $[23] !== seat.hp || $[24] !== seat.max_hp) {
    t10 = <Vitals hp={seat.hp} maxHp={seat.max_hp} block={seat.block} />;
    $[22] = seat.block;
    $[23] = seat.hp;
    $[24] = seat.max_hp;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t1 || $[27] !== t10 || $[28] !== t4 || $[29] !== t9) {
    t11 = <div className={t1}>{t4}{t9}{t10}</div>;
    $[26] = t1;
    $[27] = t10;
    $[28] = t4;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}
function PetRow(t0) {
  const $ = _c(12);
  const {
    pets,
    monsters
  } = t0;
  const t = useT();
  let t1;
  let t2;
  let t3;
  if ($[0] !== monsters || $[1] !== pets || $[2] !== t) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const live = pets.filter(_temp4);
      if (!live.length) {
        t3 = null;
        break bb0;
      }
      t1 = "mt-1 flex flex-wrap items-start justify-center gap-3";
      let t4;
      if ($[6] !== monsters || $[7] !== t) {
        t4 = (pt_0, i) => <div key={(pt_0.id || pt_0.name || "pet") + i} className="flex flex-col items-center gap-1"><EnemyCircle id={pt_0.id || ""} monsters={monsters} className="h-16 w-16 ring-2 ring-success/60" /><div className="max-w-[6rem] truncate text-xs font-medium text-success">{enemyName(pt_0, monsters) || t("Pet")}</div><Vitals hp={pt_0.hp} maxHp={pt_0.max_hp} block={pt_0.block} /></div>;
        $[6] = monsters;
        $[7] = t;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t2 = live.map(t4);
    }
    $[0] = monsters;
    $[1] = pets;
    $[2] = t;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp4(pt) {
  return pt && pt.alive !== false && (pt.id || pt.name);
}
function HudStat(t0) {
  const $ = _c(5);
  const {
    icon,
    children
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <img src={icon} alt="" className="h-5 w-5 object-contain" crossOrigin="anonymous" onError={_temp5} />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className="inline-flex items-center gap-1 text-sm font-semibold tabular-nums text-[var(--text-primary)]">{t1}{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** A clickable pile button (image + count) that opens the pile's contents. */
function _temp5(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function PileButton(t0) {
  const $ = _c(14);
  const {
    label,
    img,
    count,
    onClick,
    disabled
  } = t0;
  const t = useT();
  if (count == null) {
    return null;
  }
  let t1;
  if ($[0] !== label || $[1] !== t) {
    t1 = t("{label} pile", {
      label
    });
    $[0] = label;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== img || $[4] !== label) {
    t2 = <img src={img} alt={label} className="h-8 w-8 object-contain" crossOrigin="anonymous" />;
    $[3] = img;
    $[4] = label;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== count || $[7] !== t2) {
    t3 = <span className="inline-flex items-center gap-1 text-sm font-semibold tabular-nums text-[var(--text-secondary)]">{t2}{count}</span>;
    $[6] = count;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== disabled || $[10] !== onClick || $[11] !== t1 || $[12] !== t3) {
    t4 = <button type="button" onClick={onClick} disabled={disabled} title={t1} className="inline-flex flex-col items-center gap-0.5 hover:opacity-80 disabled:cursor-default disabled:opacity-60">{t3}</button>;
    $[9] = disabled;
    $[10] = onClick;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export default function LiveScene(t0) {
  const $ = _c(111);
  const {
    p,
    cat,
    monsters,
    encounters,
    bp,
    height: t1
  } = t0;
  const height = t1 === undefined ? "h-[80vh]" : t1;
  const t = useT();
  const t2 = p.character ?? "colorless";
  let t3;
  if ($[0] !== t2) {
    t3 = t2.toLowerCase();
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const char = t3;
  const enemies = (p.enemies ?? []).filter(_temp6);
  let t4;
  if ($[2] !== p.events || $[3] !== p.hp) {
    t4 = p.hp != null ? p.hp <= 0 : (p.events ?? []).some(_temp7);
    $[2] = p.events;
    $[3] = p.hp;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const dead = t4;
  const mates = (p.players ?? []).filter(_temp8);
  const hand = p.hand ?? [];
  const hpPct = p.hp != null && p.max_hp ? Math.max(0, Math.min(100, p.hp / p.max_hp * 100)) : 0;
  const [openCards, setOpenCards] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const groupCards = _temp9;
  const t5 = p.act_name || "";
  let t6;
  if ($[5] !== t5) {
    t6 = t5.toLowerCase();
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const region = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = new Set(["overgrowth", "underdocks", "hive", "glory"]);
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const regionBgs = t7;
  let t8;
  if ($[8] !== p.event?.id || $[9] !== p.screen || $[10] !== region) {
    t8 = p.screen === "merchant" ? ["/rooms/merchant.webp"] : p.event?.id === "NEOW" ? [imageUrl("/static/images/misc/neow.webp")] : regionBgs.has(region) ? [`/rooms/${region}.webp`] : [];
    $[8] = p.event?.id;
    $[9] = p.screen;
    $[10] = region;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const sceneLayers = t8;
  const t9 = `flex ${height} flex-col rounded-xl border border-[var(--border-subtle)]`;
  let t10;
  if ($[12] !== p.character) {
    t10 = <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[var(--accent-gold)]/50 bg-[var(--bg-primary)]"><CharacterIcon character={p.character} className="h-[88%] w-[88%]" /></span>;
    $[12] = p.character;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== p.ascension) {
    t11 = p.ascension != null && p.ascension > 0 && <span className="rounded bg-[var(--accent-gold)]/15 px-1.5 py-0.5 text-[10px] font-bold text-[var(--accent-gold)] ring-1 ring-[var(--accent-gold)]/30">A{p.ascension}</span>;
    $[14] = p.ascension;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== hpPct || $[17] !== p.hp || $[18] !== p.max_hp) {
    t12 = p.hp != null && <span className="inline-flex items-center gap-1.5"><span className="relative block h-3.5 w-24 overflow-hidden rounded bg-scrim/40 ring-1 ring-scrim/30"><span className="block h-full bg-danger-fill" style={{
          width: `${hpPct}%`
        }} /></span><span className="text-xs font-semibold tabular-nums text-danger">{p.hp}/{p.max_hp}</span></span>;
    $[16] = hpPct;
    $[17] = p.hp;
    $[18] = p.max_hp;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== p.gold) {
    t13 = p.gold != null && <HudStat icon={imageUrl("/static/images/icons/gold_icon.png")}>{p.gold}</HudStat>;
    $[20] = p.gold;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== bp || $[23] !== cat || $[24] !== p.potions) {
    t14 = (p.potions ?? []).length > 0 && <div className="flex items-center gap-1">{withOrdinalKeys(p.potions ?? []).map(t15 => {
        const {
          item,
          key
        } = t15;
        const pid = cleanId(item);
        const info = cat.potions[pid];
        return <PotionPill key={key} potionId={pid} potionData={cat.potions} bp={bp} className="block shrink-0">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={info.name} className="h-7 w-7 object-contain" crossOrigin="anonymous" onError={_temp10} /> : <span className="text-[10px] text-[var(--text-secondary)]">{displayName(`POTION.${pid}`)}</span>}</PotionPill>;
      })}</div>;
    $[22] = bp;
    $[23] = cat;
    $[24] = p.potions;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== p.act || $[27] !== t) {
    t15 = p.act != null ? t("Act {n}", {
      n: p.act
    }) : "";
    $[26] = p.act;
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== p.total_floor || $[30] !== t) {
    t16 = p.total_floor != null ? ` · ${t("F{n}", {
      n: p.total_floor
    })}` : "";
    $[29] = p.total_floor;
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t15 || $[33] !== t16) {
    t17 = <span className="text-xs font-semibold tabular-nums text-[var(--text-secondary)]">{t15}{t16}</span>;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== encounters || $[36] !== monsters || $[37] !== p.route || $[38] !== t) {
    t18 = p.route?.boss?.id && <span className="inline-flex items-center gap-1" title={t("Act boss: {name}", {
      name: enemyName(p.route.boss, monsters, encounters)
    })}><span className="text-[var(--text-muted)]">→</span><img src={imageUrl(`/static/images/misc/bosses/${p.route.boss.id.toLowerCase()}.png`)} alt={enemyName(p.route.boss, monsters, encounters) || t("Boss")} className="h-9 w-9 object-contain" crossOrigin="anonymous" onError={_temp11} /></span>;
    $[35] = encounters;
    $[36] = monsters;
    $[37] = p.route;
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t17 || $[41] !== t18) {
    t19 = <div className="flex items-center gap-1.5">{t17}{t18}</div>;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== p.map?.nodes?.length || $[44] !== setShowMap || $[45] !== t) {
    t20 = (p.map?.nodes?.length ?? 0) > 0 && <button type="button" onClick={() => setShowMap(true)} title={t("Map")} className="shrink-0 hover:opacity-80"><img src={imageUrl("/static/images/ui/top_bar/top_bar_map.png")} alt={t("Map")} className="h-10 w-10 object-contain" crossOrigin="anonymous" /></button>;
    $[43] = p.map?.nodes?.length;
    $[44] = setShowMap;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== p.deck || $[48] !== setOpenCards || $[49] !== t) {
    t21 = p.deck != null && <button type="button" onClick={() => setOpenCards({
      title: t("Deck"),
      ids: p.deck ?? []
    })} title={t("Deck")} className="relative shrink-0 hover:opacity-80"><img src={imageUrl("/static/images/ui/top_bar/top_bar_deck.png")} alt={t("Deck")} className="h-10 w-10 object-contain" crossOrigin="anonymous" />{p.deck.length > 0 && <span className="absolute -bottom-1 -right-1 rounded bg-scrim/70 px-1 text-[10px] font-bold tabular-nums text-on-fill">{p.deck.length}</span>}</button>;
    $[47] = p.deck;
    $[48] = setOpenCards;
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t20 || $[52] !== t21) {
    t22 = <div className="ml-auto flex items-center gap-2">{t20}{t21}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t10 || $[55] !== t11 || $[56] !== t12 || $[57] !== t13 || $[58] !== t14 || $[59] !== t19 || $[60] !== t22) {
    t23 = <div className="relative z-20 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-t-xl border-b border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2">{t10}{t11}{t12}{t13}{t14}{t19}{t22}</div>;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t19;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== bp || $[63] !== cat || $[64] !== p.relics) {
    t24 = (p.relics ?? []).length > 0 && <div className="relative z-30 flex flex-wrap items-center gap-1 border-b border-[var(--border-subtle)] bg-[var(--bg-card)]/60 px-3 py-1.5">{withOrdinalKeys(p.relics ?? []).map(t25 => {
        const {
          item: item_0,
          key: key_0
        } = t25;
        const rid = cleanId(item_0);
        const info_0 = cat.relics[rid];
        return <RelicPill key={key_0} relicId={rid} relicData={cat.relics} bp={bp} className="block shrink-0">{info_0?.image_url ? <img src={imageUrl(info_0.image_url)} alt={info_0.name} className="h-7 w-7 object-contain" crossOrigin="anonymous" onError={_temp12} /> : <span className="text-[10px] text-[var(--text-secondary)]">{displayName(`RELIC.${rid}`)}</span>}</RelicPill>;
      })}</div>;
    $[62] = bp;
    $[63] = cat;
    $[64] = p.relics;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== sceneLayers) {
    t25 = sceneLayers.map(_temp14);
    $[66] = sceneLayers;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== sceneLayers.length) {
    t26 = sceneLayers.length > 0 && <div className="absolute inset-0 bg-scrim/20" />;
    $[68] = sceneLayers.length;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== dead) {
    t27 = dead && <div className="absolute inset-0 bg-gradient-to-t from-danger/10 via-danger/10 to-danger/10" />;
    $[70] = dead;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t25 || $[73] !== t26 || $[74] !== t27) {
    t28 = <div className="absolute inset-0 overflow-hidden">{t25}{t26}{t27}</div>;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  const t29 = dead ? <div className="mx-auto flex max-w-lg flex-col items-center gap-3 py-10 text-center"><div className="text-xs font-bold uppercase tracking-[0.3em] text-danger">{t("Defeated")}</div>{p.death?.line && <div className="text-2xl font-semibold italic leading-snug text-danger">“<RichDescriptionSimple text={p.death.line} />”</div>}{p.death?.by && <div className="text-sm text-[var(--text-muted)]">{t("Slain by {name}", {
        name: monsterName(p.death.by, monsters)
      })}</div>}</div> : p.loot ? <div className="mx-auto max-w-2xl"><LiveLootPanel loot={p.loot} cards={cat.cards} relics={cat.relics} potions={cat.potions} bp={bp} /></div> : p.screen === "combat" && enemies.length > 0 ? <div className="flex flex-col items-center gap-8 landscape:flex-row landscape:items-start landscape:justify-between landscape:gap-6 landscape:px-[10%]"><div className="flex flex-col items-center gap-2"><span className={`inline-flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-2 bg-[var(--bg-primary)] transition ${p.turn_side === "player" ? "border-[var(--accent-gold)] ring-4 ring-[var(--accent-gold)]/70 shadow-[0_0_18px_rgba(212,175,55,0.6)]" : "border-[var(--accent-gold)]/60"}`}><CharacterIcon character={p.character} className="h-[88%] w-[88%]" /></span><div className="text-sm font-semibold text-[var(--text-primary)]">{p.username || characterName(p.character, cat.characterNames)}</div><Vitals hp={p.hp} maxHp={p.max_hp} block={p.block} /><PowerRow powers={p.player_powers ?? []} names={cat.powers} /><OrbRow orbs={p.orbs ?? []} slots={p.orb_slots} names={cat.orbs} /><PetRow pets={p.pets ?? []} monsters={monsters} />{mates.length > 0 && <div className="mt-2 flex max-w-[16rem] flex-wrap justify-center gap-4">{mates.map((s_0, i) => <PartyMate key={i} seat={s_0} turnSide={p.turn_side} names={cat.characterNames} />)}</div>}</div><div className="flex flex-wrap items-center justify-center gap-6 landscape:items-start landscape:justify-end">{enemies.map((e_5, i_0) => <div key={(e_5.id || "enemy") + i_0} className="flex flex-col items-center gap-2"><div className="min-h-[28px]"><div className="flex items-end gap-1">{(e_5.intents ?? []).map(_temp15)}</div></div><EnemyCircle id={e_5.id || ""} monsters={monsters} className={`h-28 w-28 ring-2 transition ${p.turn_side === "enemy" ? "ring-danger shadow-[0_0_18px_rgba(244,63,94,0.6)]" : "ring-danger/50"}`} /><div className="max-w-[8rem] truncate text-sm font-semibold text-[var(--text-primary)]">{enemyName(e_5, monsters)}</div><Vitals hp={e_5.hp} maxHp={e_5.max_hp} block={e_5.block} /><PowerRow powers={e_5.powers ?? []} names={cat.powers} /></div>)}</div></div> : p.event ? <div className="mx-auto max-w-2xl"><LiveEventPanel ev={p.event} bp={bp} cards={cat.cards} relics={cat.relics} events={cat.events} /></div> : p.shop ? <div className="flex items-center justify-between gap-4 px-[5%]"><span className="inline-flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[var(--accent-gold)]/60 bg-[var(--bg-primary)]"><CharacterIcon character={p.character} className="h-[88%] w-[88%]" /></span><div className="min-w-0 max-w-2xl flex-1"><LiveShopPanel shop={p.shop} cards={cat.cards} relics={cat.relics} potions={cat.potions} bp={bp} /></div><span className="inline-flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[var(--accent-gold)]/60 bg-[var(--bg-primary)]"><img src={imageUrl("/static/images/misc/merchant.webp")} alt={t("Merchant")} className="h-[88%] w-[88%] object-contain" crossOrigin="anonymous" /></span></div> : p.screen === "rest" ? <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-8 text-center"><span className="inline-flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-[var(--accent-gold)]/60 bg-[var(--bg-primary)]"><CharacterIcon character={p.character} className="h-[88%] w-[88%]" /></span><div className="text-base font-semibold text-warning">{t("Resting at a campfire")}</div>{p.hp != null && <div className="text-sm text-[var(--text-secondary)] tabular-nums">{t("{hp}/{max} HP", {
        hp: p.hp,
        max: p.max_hp ?? "?"
      })}</div>}{p.rest?.options?.length ? <div className="flex flex-wrap justify-center gap-2">{p.rest.options.map(_temp16)}</div> : null}</div> : <div className="py-12 text-center text-sm text-on-fill/70">{p.screen ? t("On the {screen} screen", {
      screen: screenLabel(p.screen, t)
    }) : t("Between rooms")}</div>;
  let t30;
  if ($[76] !== t29) {
    t30 = <div className="relative w-full px-6 py-8">{t29}</div>;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t28 || $[79] !== t30) {
    t31 = <div className="relative flex flex-1 items-center bg-gradient-to-b from-[#1a1320] via-[#120c18] to-[#0c0810]">{t28}{t30}</div>;
    $[78] = t28;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  const t32 = (hand.length > 0 || p.energy != null || p.draw_count != null || p.discard_count != null) && <div className="flex items-end justify-between gap-3 rounded-b-xl border-t border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-3"><div className="flex shrink-0 items-center gap-3"><PileButton label={t("Draw")} img={imageUrl("/static/images/ui/combat/draw_pile.png")} count={p.draw_count} onClick={() => setOpenCards({
        title: t("{label} pile", {
          label: t("Draw")
        }),
        ids: p.draw_pile ?? []
      })} disabled={!p.draw_pile?.length} />{p.energy != null && <span className="inline-flex items-center gap-1 text-base font-bold tabular-nums text-[var(--text-primary)]"><img src={imageUrl(`/static/images/icons/${char}_energy_icon.png`)} alt={t("Energy")} className="h-8 w-8 object-contain" crossOrigin="anonymous" onError={_temp17} />{p.energy}{p.max_energy != null ? `/${p.max_energy}` : ""}</span>}</div><div className="flex flex-1 flex-wrap items-end justify-center gap-1.5">{withOrdinalKeys(hand).map(t33 => {
        const {
          item: item_1,
          key: key_1
        } = t33;
        const {
          id,
          upgraded
        } = parseDeckId(item_1);
        return <CardPill key={key_1} cardId={id} upgraded={upgraded} cardData={cat.cards} bp={bp} className="relative block w-20 shrink-0 transition hover:-translate-y-2"><LiveCardImg id={id} upgraded={upgraded} alt={cat.cards[id]?.name || displayName(`CARD.${id}`)} className="h-auto w-20 rounded-sm" portrait={cat.cards[id]?.image_url} /></CardPill>;
      })}</div><div className="flex shrink-0 items-center gap-3"><PileButton label={t("Discard")} img={imageUrl("/static/images/ui/combat/discard_pile.png")} count={p.discard_count} onClick={() => setOpenCards({
        title: t("{label} pile", {
          label: t("Discard")
        }),
        ids: p.discard_pile ?? []
      })} disabled={!p.discard_pile?.length} /><PileButton label={t("Exhaust")} img={imageUrl("/static/images/ui/combat/exhaust_pile.png")} count={p.exhaust_count} onClick={() => setOpenCards({
        title: t("{label} pile", {
          label: t("Exhaust")
        }),
        ids: p.exhaust_pile ?? []
      })} disabled={!p.exhaust_pile?.length} /></div></div>;
  let t34;
  if ($[81] !== bp || $[82] !== cat || $[83] !== groupCards || $[84] !== openCards || $[85] !== setOpenCards || $[86] !== t) {
    t34 = openCards && <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-scrim/60 p-4" onClick={() => setOpenCards(null)}><div className="my-8 w-full max-w-3xl rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" onClick={_temp18}><div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-semibold text-[var(--accent-gold)]">{openCards.title} ({openCards.ids.length})</h3><button type="button" onClick={() => setOpenCards(null)} aria-label={t("Close")} className="px-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">✕</button></div>{openCards.ids.length === 0 ? <p className="text-sm text-[var(--text-muted)]">{t("No cards on this beat.")}</p> : <div className="flex flex-wrap gap-2">{groupCards(openCards.ids).map(t35 => {
            const [raw_0, count] = t35;
            const {
              id: id_0,
              upgraded: upgraded_0
            } = parseDeckId(raw_0);
            return <CardPill key={raw_0} cardId={id_0} upgraded={upgraded_0} cardData={cat.cards} bp={bp} className="relative block w-24 shrink-0"><LiveCardImg id={id_0} upgraded={upgraded_0} alt={cat.cards[id_0]?.name || displayName(`CARD.${id_0}`)} className="h-auto w-24 rounded-sm" portrait={cat.cards[id_0]?.image_url} />{count > 1 && <span className="absolute -top-1 -right-1 rounded bg-[var(--accent-gold)] px-1 text-[10px] font-bold text-[var(--bg-primary)]">×{count}</span>}</CardPill>;
          })}</div>}</div></div>;
    $[81] = bp;
    $[82] = cat;
    $[83] = groupCards;
    $[84] = openCards;
    $[85] = setOpenCards;
    $[86] = t;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== cat || $[89] !== encounters || $[90] !== monsters || $[91] !== p.act_name || $[92] !== p.character || $[93] !== p.floor_history || $[94] !== p.map || $[95] !== p.path || $[96] !== p.pos || $[97] !== p.reveals || $[98] !== p.route || $[99] !== setShowMap || $[100] !== showMap || $[101] !== t) {
    t35 = showMap && <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-scrim/60 p-4" onClick={() => setShowMap(false)}><div className="my-8 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" onClick={_temp19}><div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-semibold text-[var(--accent-gold)]">{p.map?.act != null ? t("Map \xB7 Act {n}", {
              n: p.map.act
            }) : t("Map")}</h3><button type="button" onClick={() => setShowMap(false)} aria-label={t("Close")} className="px-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">✕</button></div><LiveMap map={p.map} path={p.path} pos={p.pos} reveals={p.reveals} route={p.route} monsters={monsters} encounters={encounters} floorHistory={p.floor_history} cat={cat} actName={p.act_name} character={p.character} /></div></div>;
    $[88] = cat;
    $[89] = encounters;
    $[90] = monsters;
    $[91] = p.act_name;
    $[92] = p.character;
    $[93] = p.floor_history;
    $[94] = p.map;
    $[95] = p.path;
    $[96] = p.pos;
    $[97] = p.reveals;
    $[98] = p.route;
    $[99] = setShowMap;
    $[100] = showMap;
    $[101] = t;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== t23 || $[104] !== t24 || $[105] !== t31 || $[106] !== t32 || $[107] !== t34 || $[108] !== t35 || $[109] !== t9) {
    t36 = <div className={t9}>{t23}{t24}{t31}{t32}{t34}{t35}</div>;
    $[103] = t23;
    $[104] = t24;
    $[105] = t31;
    $[106] = t32;
    $[107] = t34;
    $[108] = t35;
    $[109] = t9;
    $[110] = t36;
  } else {
    t36 = $[110];
  }
  return t36;
}
function _temp19(ev_0) {
  return ev_0.stopPropagation();
}
function _temp18(ev) {
  return ev.stopPropagation();
}
function _temp17(e_6) {
  const el = e_6.target as HTMLImageElement;
  const fb = imageUrl("/static/images/icons/colorless_energy_icon.png");
  if (el.src !== fb) {
    el.src = fb;
  }
}
function _temp16(o) {
  return <span key={o.id} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${o.enabled === false ? "border-[var(--border-subtle)] text-[var(--text-muted)] opacity-50" : "border-[var(--accent-gold)]/50 bg-[var(--bg-card)]/80 text-[var(--text-primary)]"}`}>{o.title || displayName(`REST.${o.id}`)}</span>;
}
function _temp15(it, j) {
  return <IntentBadge key={j} intent={it} />;
}
function _temp14(src) {
  return <img key={src} src={src} alt="" className="absolute inset-0 h-full w-full object-cover" crossOrigin="anonymous" onError={_temp13} />;
}
function _temp13(e_4) {
  (e_4.target as HTMLImageElement).style.display = "none";
}
function _temp12(e_3) {
  (e_3.target as HTMLImageElement).style.display = "none";
}
function _temp11(e_2) {
  (e_2.target as HTMLImageElement).style.display = "none";
}
function _temp10(e_1) {
  (e_1.target as HTMLImageElement).style.display = "none";
}
function _temp9(ids) {
  const m = new Map();
  for (const raw of ids) {
    m.set(raw, (m.get(raw) ?? 0) + 1);
  }
  return [...m.entries()];
}
function _temp8(s) {
  return !s.is_me;
}
function _temp7(e_0) {
  return e_0.k === "death";
}
function _temp6(e) {
  return (e.hp ?? 1) > 0;
}